import React, { useState } from 'react';
import { I18n } from '@aws-amplify/core';
import { Col, Empty, Skeleton } from 'antd';

import { CloseIcon } from '@/assets/icons/v2';
import { Refetch } from '@/components/ui/Refetch';
import { getVersionLabel } from '@/views/Bera/utils';
import { ModalDelete } from '@/components/ui/Modals/ModalDelete';
import { useDeleteBeraJobSummary, useGetBeraVersionList } from '@/hooks';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useDownloadPDF } from '@/components/views/BeraJob/Results/hooks';
import { DATE_SPAN, MODAL_CLOSE_ICON_SIZE, MODAL_LARGE_WIDTH, TLID_SPAN, VERSION_SPAN } from '@/views/Bera/constants';

import type { BeraJobSummaryListItem, BeraVersion } from '@/types/Bera';
import { VersionItem } from './VersionItem';
import * as S from './styles';

interface ModalVersionsProps {
	open: boolean;
	report: BeraJobSummaryListItem;
	onCancel: () => void;
	onShowTlids: (version: BeraVersion, label: string) => void;
}

export function ModalVersions({ open, report, onCancel, onShowTlids }: Readonly<ModalVersionsProps>): JSX.Element {
	const { organization, company } = useApplicationContext();

	const [versionToDelete, setVersionToDelete] = useState<BeraVersion | null>(null);

	const params = { id: report.id, organization_id: organization?.id, company_id: company?.id };
	const { data, isInitialLoading, isError, refetch } = useGetBeraVersionList(params);
	const { mutate: downloadPDF } = useDownloadPDF();
	const { mutate: deleteJobSummary, isLoading: isDeleting } = useDeleteBeraJobSummary();

	const versions = data ?? [];
	const latestIndex = versions.length - 1;

	function handleDownload(version: BeraVersion): void {
		const payload = {
			organization_id: organization?.id,
			company_id: company?.id,
			bera_job_summary_id: version.id,
			locale: window.navigator.language
		};

		downloadPDF(payload);
	}

	function handleOpenDelete(version: BeraVersion): void {
		setVersionToDelete(version);
	}

	function handleCloseDelete(): void {
		setVersionToDelete(null);
	}

	function handleConfirmDelete(): void {
		if (!versionToDelete) {
			return;
		}

		const payload = {
			id: versionToDelete.id,
			organization_id: organization?.id,
			company_id: company?.id
		};

		const isAnchorVersion = versionToDelete.id === report.id;

		deleteJobSummary(payload, { onSuccess: handleCloseDelete });

		if (isAnchorVersion) {
			onCancel();
		}
	}

	function renderContent(): JSX.Element {
		if (isInitialLoading) {
			return <Skeleton active paragraph={{ rows: 3 }} />;
		}

		if (isError) {
			return <Refetch message="Failed to load the versions." onClick={refetch} />;
		}

		if (!versions.length) {
			return <Empty description={I18n.get('No version found')} />;
		}

		return (
			<>
				<S.HeaderRow align="middle">
					<Col span={VERSION_SPAN}>
						<S.HeaderLabel>{I18n.get('Version')}</S.HeaderLabel>
					</Col>
					<Col span={TLID_SPAN}>
						<S.HeaderLabel>{I18n.get('TLID')}</S.HeaderLabel>
					</Col>
					<Col span={DATE_SPAN}>
						<S.HeaderLabel>{I18n.get('Date')}</S.HeaderLabel>
					</Col>
				</S.HeaderRow>
				{versions.map((version, index) => (
					<VersionItem
						key={version.id}
						version={version}
						latest={index === latestIndex}
						current={version.is_current}
						label={getVersionLabel(index)}
						onShowTlids={onShowTlids}
						onDelete={handleOpenDelete}
						onDownload={handleDownload}
					/>
				))}
			</>
		);
	}

	return (
		<>
			<S.ModalLarge
				centered
				open={open}
				footer={null}
				keyboard={false}
				onCancel={onCancel}
				maskClosable={false}
				width={MODAL_LARGE_WIDTH}
				title={I18n.get('Versions')}
				closeIcon={<CloseIcon width={MODAL_CLOSE_ICON_SIZE} height={MODAL_CLOSE_ICON_SIZE} />}
			>
				{renderContent()}
			</S.ModalLarge>
			<ModalDelete
				open={!!versionToDelete}
				loading={isDeleting}
				onCancel={handleCloseDelete}
				onConfirm={handleConfirmDelete}
				description={I18n.get('Heads up! Want to delete this report?')}
			/>
		</>
	);
}
